import React, { useState, useContext } from 'react';
import { ArrowRight, Lightbulb } from 'lucide-react';
import { BasketIcon } from '../../BasketIcon';
import { UserContext } from '../../../UserContext';
import {
  shouldShowRotationalOverlay,
  applyPlacementCelebrationContinue,
  applyPlacementCelebrationRemind,
} from '../../../lib/basketPlacementCelebration';
import BasketPlacementCelebrationOverlay from './BasketPlacementCelebrationOverlay';

const RelationDescriptionModal = ({
  onClose,
  onSubmit,
  onComplete,
  topic,
  basketName,
  basketTopics = [],
}) => {
  const { user } = useContext(UserContext);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);

  const movedTopicName = topic?.name || 'this topic';
  const isInteractionLocked = isSubmitting || showSuccessIndicator;

  const handleCelebrationDismiss = () => {
    setShowSuccessIndicator(false);
    setIsSubmitting(false);
    onComplete();
  };

  const handleContinue = () => {
    applyPlacementCelebrationContinue(user?.id);
    handleCelebrationDismiss();
  };

  const handleRemindAgain = () => {
    applyPlacementCelebrationRemind(user?.id);
    handleCelebrationDismiss();
  };

  const runSubmit = async (relationDescription) => {
    if (isSubmitting || showSuccessIndicator) return;

    setIsSubmitting(true);
    try {
      await onSubmit(relationDescription);
      if (!shouldShowRotationalOverlay(user?.id)) {
        setIsSubmitting(false);
        onComplete();
        return;
      }
      setShowSuccessIndicator(true);
      setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    runSubmit(description);
  };

  const handleSkip = () => {
    runSubmit('');
  };

  // Get topic's highlighted concept or first concept
  const topicConcept = topic?.concepts?.find(c => c.is_highlighted) || topic?.concepts?.[0];
  const topicConceptText = topicConcept?.text || '';

  // Get tag color with fallbacks
  const tagColor = topic?.tag?.color || topic?.tags?.color || '#981F2B'; // Default to cranberry
  const backgroundColor = tagColor ? `${tagColor}70` : 'rgba(229, 231, 235, 0.7)'; // 70 = 44% opacity
  
  // Helper function to darken color
  const darkenColor = (color, amount = 30) => {
    if (!color || typeof color !== 'string') return '#981F2B';
    try {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const factor = (100 - amount) / 100;
      const newR = Math.floor(Math.max(0, r * factor));
      const newG = Math.floor(Math.max(0, g * factor));
      const newB = Math.floor(Math.max(0, b * factor));
      return `#${((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)}`;
    } catch (error) {
      return '#981F2B';
    }
  };
  
  const borderColor = tagColor ? darkenColor(tagColor) : '#d1d5db';

  // Get all items (only topics, not basket name)
  const allItems = basketTopics;
  const hasDescription = description.length > 0;
  const charCount = description.length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="relative bg-white rounded-2xl max-w-[700px] w-full shadow-2xl overflow-hidden my-4">
        {showSuccessIndicator && (
          <BasketPlacementCelebrationOverlay
            embedded
            userId={user?.id}
            movedTopicName={movedTopicName}
            onContinue={handleContinue}
            onRemindAgain={handleRemindAgain}
          />
        )}

        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-b from-white to-gray-50">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">Create your personal link</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Make it meaningful to you</p>
            </div>
            <button
              onClick={onClose}
              disabled={isInteractionLocked}
              className={`
                text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded-lg hover:bg-gray-100 transition-colors
                ${isInteractionLocked ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>

        {/* Horizontal Container - More Compact */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 bg-gradient-to-b from-white to-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_auto_1.8fr] gap-4 md:gap-4 items-center min-h-[200px]">
            
            {/* Left: Topic */}
            <div className="flex flex-col items-center justify-center relative px-2 order-1 md:order-1">
              {/* Thought Bubble - Positioned above topic card */}
              {description && (
                <div className="absolute -top-24 sm:-top-28 left-1/2 -translate-x-1/2 bg-gradient-to-br from-white to-rose-50 border-2 border-rose-800 rounded-2xl px-3 sm:px-4 py-2 sm:py-3 max-w-[200px] sm:max-w-[240px] shadow-lg z-10 transition-all">
                  <div className="text-xs sm:text-sm text-gray-700 font-medium leading-relaxed text-center">
                    {description || 'Start typing to see your connection...'}
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[12px] border-l-transparent border-r-transparent border-t-rose-800"></div>
                  <div className="absolute -bottom-[11px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-rose-50"></div>
                </div>
              )}

              {/* Topic Card - Using app styling */}
              <div className="flex flex-col items-center w-full max-w-[240px] mx-auto p-3 sm:p-4 bg-white rounded-xl border border-neutral-900 shadow-sm max-h-[280px] sm:max-h-[320px]">
                {/* Topic name in pill with tag color - Smaller */}
                <div className="w-full mb-3 flex justify-center flex-shrink-0">
                  <span 
                    className="inline-block px-2.5 py-1 rounded-full font-medium text-black text-xs max-w-full break-words text-center"
                    style={{ 
                      backgroundColor: backgroundColor,
                      border: `1px solid ${borderColor}`,
                      lineHeight: '1.2',
                    }}
                  >
                    {topic?.name || ''}
                  </span>
                </div>
                
                {/* Highlighted concept - Bigger, more prominent - Scrollable */}
                {topicConcept && (
                  <div className="w-full flex-grow flex justify-center overflow-y-auto min-h-0">
                    <div 
                      className="w-auto max-w-[95%] text-left leading-relaxed text-sm font-medium pr-1" 
                      style={{ 
                        color: topicConcept.is_highlighted ? '#0066cc' : '#666666',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      {topicConceptText}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Center: Arrow - Smaller */}
            <div className="flex items-center justify-center px-2 order-2 md:order-2 my-4 md:my-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-rose-800 to-rose-900 flex items-center justify-center shadow-lg relative rotate-90 md:rotate-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-800 to-rose-900 opacity-20 animate-ping"></div>
                <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-white relative z-10" />
              </div>
            </div>

            {/* Right: Basket - Using app styling */}
            <div className="flex flex-col justify-center px-2 sm:px-3 order-3 md:order-3">
              <div className="bg-white border-2 border-neutral-900 rounded-xl p-3 sm:p-4 pt-2 h-full shadow-sm flex flex-col">
                {/* Basket Header */}
                <div className="flex items-start space-x-2 mb-3 sm:mb-4">
                  <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 mt-1 text-amber-600">
                    <BasketIcon />
                  </div>
                  <h2 className="text-base sm:text-lg font-medium text-black break-words min-w-0 flex-grow">
                    {basketName || ''}
                  </h2>
                </div>

                {/* Items List - 2 Column Grid like BasketCard */}
                <div 
                  className="grid grid-cols-2 gap-1.5 sm:gap-2 max-h-[180px] sm:max-h-[220px] overflow-y-auto pr-1 flex-1"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {allItems.map((item, index) => {
                    const highlightedConcept = item.concepts?.find(c => c.is_highlighted) || item.concepts?.[0];
                    
                    // Get tag color for item
                    const itemTagColor = item?.tag?.color || item?.tags?.color || '#981F2B';
                    const itemBackgroundColor = itemTagColor ? `${itemTagColor}70` : 'rgba(229, 231, 235, 0.7)';
                    const itemBorderColor = itemTagColor ? darkenColor(itemTagColor) : '#d1d5db';

                    return (
                      <div
                        key={item.id || index}
                        className="flex flex-col items-center w-full h-full p-1 overflow-hidden rounded-md"
                      >
                        {/* Topic name in pill with tag color */}
                        <div className="w-full mb-1 flex justify-center">
                          <span 
                            className="inline-block px-2 py-1 rounded-full font-medium text-black w-auto max-w-full break-words text-center text-xs"
                            style={{ 
                              backgroundColor: itemBackgroundColor,
                              border: `1px solid ${itemBorderColor}`,
                              lineHeight: '1.2',
                            }}
                          >
                            {item.name}
                          </span>
                        </div>
                        
                        {/* Highlighted concept - blue when highlighted */}
                        {highlightedConcept && (
                          <div className="w-full flex-grow flex justify-center">
                            <div 
                              className="w-auto max-w-[90%] text-left leading-tight text-xs" 
                              style={{ 
                                color: highlightedConcept.is_highlighted ? '#0066cc' : '#666666' 
                              }}
                            >
                              {highlightedConcept.text}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Input Area - More Prominent */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-t-2 border-gray-200 bg-white">
          <label className="block text-sm sm:text-base font-bold text-gray-900 mb-2 sm:mb-3">
            Why is {topic?.name || 'this topic'} in {basketName || 'this basket'}?
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute right-3 top-3 text-[10px] sm:text-xs text-gray-400">
              {charCount} / 250
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Your unique connection - no right answer, just what works for YOU"
              className="w-full px-4 pr-16 sm:px-5 sm:pr-20 py-3 sm:py-4 border-2 border-gray-300 rounded-xl text-sm sm:text-[15px] text-gray-900 resize-y transition-all focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-200 leading-relaxed bg-white shadow-sm min-h-[120px] sm:min-h-[140px]"
              rows={5}
              maxLength={250}
              autoFocus
              disabled={isInteractionLocked}
            />
          </div>
          <div
            className="
              mt-2 sm:mt-3 rounded-lg border border-butter-300 border-l-4 border-l-butter-500
              bg-butter-50 p-2 sm:p-3 shadow-sm text-neutral-900
            "
            role="note"
            aria-label="Pro tip: reference another topic in the basket or the basket name"
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <Lightbulb
                className="mt-0.5 h-4 w-4 shrink-0 text-butter-600 sm:h-5 sm:w-5"
                aria-hidden="true"
                strokeWidth={2}
              />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-xs font-semibold uppercase tracking-wide text-butter-800">
                  Pro tip:
                </div>
                <p className="m-0 text-xs sm:text-sm font-normal leading-relaxed text-neutral-900">
                  Reference the other topic inside the basket or the basket name itself. Try{' '}
                  <span className="italic">&ldquo;Like [Topic A], this…&rdquo;</span>
                  {' '}or{' '}
                  <span className="italic">&ldquo;Sounds like [Topic A]&rdquo;</span>
                  {' '}or{' '}
                  <span className="italic">&ldquo;[Topic A] causes this&rdquo;</span>
                  {' '}or{' '}
                  <span className="italic">&ldquo;Located with [Topic A]&rdquo;</span>
                  {' '}or{' '}
                  <span className="italic">&ldquo;Looks similar to [Topic A]&rdquo;</span>
                  {' '}or{' '}
                  <span className="italic">&ldquo;Associated with [Topic A].&rdquo;</span>{' '}
                  Future you will know exactly why they belong together.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 bg-white">
          <button
            type="button"
            onClick={handleSkip}
            disabled={isInteractionLocked}
            className={`
              text-gray-600 text-sm font-semibold hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-center
              ${isInteractionLocked ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {isSubmitting ? 'Moving...' : 'Maybe later'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!hasDescription || isInteractionLocked}
            className={`
              px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm font-bold transition-all shadow-lg text-center
              ${hasDescription && !isInteractionLocked
                ? 'bg-gradient-to-br from-rose-800 to-rose-900 text-white hover:-translate-y-0.5 hover:shadow-xl'
                : 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {isSubmitting ? 'Moving...' : 'Create your personal link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelationDescriptionModal;
